import {
  dayKey, dayStartOf, daysBetween, GOOGLE_PROJECT_KEY, GOOGLE_TASK_KEY, shiftDays, type EditScope, type GoogleCalendar, type GoogleChange, type GoogleEvent,
  type GoogleTime, type GoogleWrite, type RepeatRule
} from "@otrack/domain";
import { dateBasic, endRuleBefore, parseRecurrence, ruleFrom, ruleStarts, rruleLine, utcBasic, withRule, type Recurrence } from "./recurrence";

/**
 * Google events as the device keeps them (ADR 0027): each calendar's events by id, repeating
 * ones as a series plus exceptions, expanded here like Events. Every change is built as the
 * Google request to send plus the event as it should look meanwhile, so the Calendar shows it at
 * once and offline, and as the event before it, so Undo can send the reverse.
 */

/** Events of each calendar by id. */
export type GoogleEvents = Record<string, Record<string, GoogleEvent>>;

/** A change waiting to reach Google. */
export interface PendingChange {
  id: string;
  change: GoogleChange;
  /** The event as the device shows it until Google answers; null once it is gone. */
  local: GoogleEvent | null;
  /** The event before the change, for Undo; null when the change created it. */
  before: GoogleEvent | null;
  /** The event's title, for notices about the change. */
  label: string;
}

/** Fields of an occurrence a change may set. Times are the occurrence's new span. */
export interface GooglePatch {
  title?: string;
  notes?: string;
  location?: string;
  projectId?: string | null;
  taskId?: string | null;
  allDay?: boolean;
  start?: number;
  end?: number;
  repeat?: RepeatRule | null;
}

export interface GoogleNewEvent {
  title: string;
  notes: string;
  location: string;
  projectId: string | null;
  taskId: string | null;
  allDay: boolean;
  start: number;
  end: number;
  repeat: RepeatRule | null;
}

/** One appearance of a Google event on the Calendar. */
export interface GoogleOccurrence {
  /** Unique on the Calendar: calendar and the id of this occurrence. */
  key: string;
  calendarId: string;
  /** The event whose fields this occurrence shows: the exception, the series, or a single event. */
  event: GoogleEvent;
  /** The series, when the occurrence repeats and the series is known here. */
  master: GoogleEvent | null;
  /** The series' id, or the event's own when it doesn't repeat. Logged time links to it. */
  seriesId: string;
  /** The id that changes "this event" only: the exception's, or the one Google gives the occurrence. */
  instanceId: string;
  /** Original start of a repeating occurrence; null for single events. */
  originalStart: number | null;
  start: number;
  end: number;
  allDay: boolean;
  recurring: boolean;
}

/* ---------------------------------------------------------------- times */

export function timeOf(time: GoogleTime | undefined): { at: number; allDay: boolean } | null {
  if (!time) return null;
  if (time.date) return { at: dayStartOf(time.date), allDay: true };
  if (time.dateTime) {
    const at = Date.parse(time.dateTime);
    return Number.isNaN(at) ? null : { at, allDay: false };
  }
  return null;
}

/** Start and end as instants. All-day events span whole local days; Google's end date is exclusive. */
export function spanOf(event: GoogleEvent): { start: number; end: number; allDay: boolean } | null {
  const start = timeOf(event.start);
  if (!start) return null;
  const end = timeOf(event.end);
  const shortest = start.allDay ? shiftDays(start.at, 1) : start.at;
  return { start: start.at, end: end && end.at >= shortest ? end.at : shortest, allDay: start.allDay };
}

/** The device's time zone, which new timed events are written in. */
export function deviceZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * A time for Google. `clear` names the other form's fields as null, since a patch merges `start`
 * with what Google has, and an event can't have both a date and a time.
 */
function writeTime(ts: number, allDay: boolean, zone: string, clear: boolean): NonNullable<GoogleWrite["start"]> {
  if (allDay) return clear ? { date: dayKey(ts), dateTime: null, timeZone: null } : { date: dayKey(ts) };
  return clear ? { dateTime: new Date(ts).toISOString(), timeZone: zone, date: null } : { dateTime: new Date(ts).toISOString(), timeZone: zone };
}

/** How a time reads back once written: what the device shows until Google answers. */
function readTime(time: NonNullable<GoogleWrite["start"]>): GoogleTime {
  const result: GoogleTime = {};
  if (time.date) result.date = time.date;
  if (time.dateTime) result.dateTime = time.dateTime;
  if (time.timeZone) result.timeZone = time.timeZone;
  return result;
}

/** Google's id for one occurrence of a series: the series id and the occurrence's original start. */
export function instanceId(seriesId: string, original: number, allDay: boolean): string {
  return `${seriesId}_${allDay ? dayKey(original).replace(/-/g, "") : utcBasic(original)}`;
}

/** A new event id in Google's alphabet (base32hex). */
export function newGoogleId(): string {
  const alphabet = "0123456789abcdefghijklmnopqrstuv";
  const bytes = crypto.getRandomValues(new Uint8Array(26));
  return [...bytes].map((byte) => alphabet[byte % 32]).join("");
}

/* ---------------------------------------------------------------- reading events */

/** Events that never show on the Calendar: deleted ones, and Google's "working location" markers. */
function hidden(event: GoogleEvent): boolean {
  return event.status === "cancelled" || event.eventType === "workingLocation";
}

export function recurrenceOf(event: GoogleEvent): Recurrence {
  return parseRecurrence(event.recurrence, spanOf(event)?.start ?? 0);
}

/** The ontrack Project and Task kept on the event. */
export function projectOf(event: GoogleEvent): { projectId: string | null; taskId: string | null } {
  const props = event.extendedProperties?.private ?? {};
  return { projectId: props[GOOGLE_PROJECT_KEY] || null, taskId: props[GOOGLE_TASK_KEY] || null };
}

/**
 * Whether ontrack may change the event: the calendar must allow writing, it must be an ordinary
 * event (not a birthday or one made from Gmail), and an invitation only when the organizer lets
 * guests change it.
 */
export function canEdit(event: GoogleEvent, calendar: GoogleCalendar | undefined): boolean {
  if (!calendar || (calendar.accessRole !== "owner" && calendar.accessRole !== "writer")) return false;
  if (event.eventType && event.eventType !== "default") return false;
  const invited = event.organizer?.self !== true && (event.attendees ?? []).some((person) => person.self && !person.organizer);
  return !invited || event.guestsCanModify === true;
}

/** Every occurrence of a calendar's events that overlaps [from, to), with exceptions and removals applied. */
export function googleOccurrences(calendarId: string, events: Readonly<Record<string, GoogleEvent>>, from: number, to: number): GoogleOccurrence[] {
  const overlaps = (start: number, end: number) => start < to && (end > from || (end === start && start >= from));
  const exceptions = new Map<string, GoogleEvent[]>();
  for (const event of Object.values(events)) {
    if (!event.recurringEventId) continue;
    exceptions.set(event.recurringEventId, [...(exceptions.get(event.recurringEventId) ?? []), event]);
  }
  const result: GoogleOccurrence[] = [];
  const push = (event: GoogleEvent, master: GoogleEvent | null, seriesId: string, id: string, originalStart: number | null, span: { start: number; end: number; allDay: boolean }) => {
    if (!overlaps(span.start, span.end)) return;
    result.push({ key: `${calendarId}|${id}`, calendarId, event, master, seriesId, instanceId: id, originalStart, ...span, recurring: originalStart !== null });
  };

  for (const event of Object.values(events)) {
    if (event.recurringEventId) {
      // An exception whose series isn't here (or no longer matches it) still shows on its own.
      const master = events[event.recurringEventId];
      if (master && !hidden(master) && recurrenceOf(master).rule) continue;
      if (master && hidden(master)) continue;
      const span = spanOf(event);
      if (!span || hidden(event)) continue;
      push(event, null, event.recurringEventId, event.id, timeOf(event.originalStartTime)?.at ?? span.start, span);
      continue;
    }
    if (hidden(event)) continue;
    const span = spanOf(event);
    if (!span) continue;
    const { rule, exdates } = recurrenceOf(event);
    if (!rule) { push(event, null, event.id, event.id, null, span); continue; }

    const own = exceptions.get(event.id) ?? [];
    const byStart = new Map<number, GoogleEvent>();
    const byDay = new Map<string, GoogleEvent>();
    for (const exception of own) {
      const original = timeOf(exception.originalStartTime)?.at;
      if (original === undefined) continue;
      byStart.set(original, exception);
      byDay.set(dayKey(original), exception);
    }
    const used = new Set<GoogleEvent>();
    const excluded = new Set(exdates);
    const length = span.end - span.start;
    const lengthDays = span.allDay ? daysBetween(span.start, span.end) : 0;
    for (const original of ruleStarts(span.start, rule, to)) {
      if (excluded.has(original)) continue;
      // The series expands on this device's clock; matching by day keeps exceptions in place when
      // the series was written in another time zone.
      const exception = byStart.get(original) ?? byDay.get(dayKey(original));
      if (exception) {
        used.add(exception);
        const exceptionSpan = spanOf(exception);
        if (hidden(exception) || !exceptionSpan) continue;
        push(exception, event, event.id, exception.id, original, exceptionSpan);
        continue;
      }
      const end = span.allDay ? shiftDays(original, lengthDays) : original + length;
      push(event, event, event.id, instanceId(event.id, original, span.allDay), original, { start: original, end, allDay: span.allDay });
    }
    for (const exception of own) {
      const exceptionSpan = spanOf(exception);
      if (used.has(exception) || hidden(exception) || !exceptionSpan) continue;
      push(exception, event, event.id, exception.id, timeOf(exception.originalStartTime)?.at ?? exceptionSpan.start, exceptionSpan);
    }
  }
  return result.sort((a, b) => a.start - b.start);
}

/* ---------------------------------------------------------------- applying changes locally */

/** The event with written fields applied, as Google would merge a patch. */
export function withFields(event: GoogleEvent, fields: GoogleWrite): GoogleEvent {
  const next: GoogleEvent = { ...event };
  if (fields.summary !== undefined) next.summary = fields.summary;
  if (fields.description !== undefined) next.description = fields.description;
  if (fields.location !== undefined) next.location = fields.location;
  if (fields.start) next.start = readTime(fields.start);
  if (fields.end) next.end = readTime(fields.end);
  if (fields.recurrence !== undefined) {
    if (fields.recurrence.length) next.recurrence = fields.recurrence;
    else delete next.recurrence;
  }
  if (fields.status) next.status = fields.status;
  if (fields.extendedProperties) {
    const merged: Record<string, string> = { ...event.extendedProperties?.private };
    for (const [key, value] of Object.entries(fields.extendedProperties.private)) {
      if (value) merged[key] = value;
      else delete merged[key];
    }
    next.extendedProperties = { private: merged };
  }
  return next;
}

function setEvent(events: GoogleEvents, calendarId: string, event: GoogleEvent): void {
  events[calendarId] = { ...events[calendarId], [event.id]: event };
}

function removeEvent(events: GoogleEvents, calendarId: string, id: string): void {
  const calendar = { ...events[calendarId] };
  delete calendar[id];
  // A series goes with its exceptions.
  for (const event of Object.values(calendar)) if (event.recurringEventId === id) delete calendar[event.id];
  events[calendarId] = calendar;
}

/** Puts one change's outcome into `events`: what Google answered, or what the device expects. */
export function placeChange(events: GoogleEvents, change: GoogleChange, result: GoogleEvent | null): void {
  if (change.type === "move") {
    removeEvent(events, change.calendarId, change.eventId);
    if (result) setEvent(events, change.destination, result);
    return;
  }
  if (result) {
    // A restored event Google could only recreate comes back under a new id.
    if (result.id !== change.eventId) removeEvent(events, change.calendarId, change.eventId);
    setEvent(events, change.calendarId, result);
  } else removeEvent(events, change.calendarId, change.eventId);
}

/** What the device shows: Google's events with every waiting change applied in order. */
export function applyPending(server: GoogleEvents, pending: readonly PendingChange[]): GoogleEvents {
  if (!pending.length) return server;
  const events: GoogleEvents = { ...server };
  for (const item of pending) placeChange(events, item.change, item.local);
  return events;
}

/**
 * Merges what Google sent. A first sync replaces the calendar; later ones bring only changes,
 * with deleted events marked cancelled. Cancelled exceptions are kept: they remove occurrences.
 */
export function mergeEvents(current: Readonly<Record<string, GoogleEvent>> | undefined, incoming: readonly GoogleEvent[], full: boolean): Record<string, GoogleEvent> {
  const events: GoogleEvents = { calendar: full ? {} : { ...current } };
  for (const event of incoming) {
    if (event.status === "cancelled" && !event.recurringEventId) removeEvent(events, "calendar", event.id);
    else setEvent(events, "calendar", event);
  }
  return events["calendar"]!;
}

/* ---------------------------------------------------------------- building changes */

function contentFields(event: GoogleEvent): GoogleWrite {
  const fields: GoogleWrite = {
    summary: event.summary ?? "",
    description: event.description ?? "",
    location: event.location ?? ""
  };
  if (event.start) fields.start = { ...event.start };
  if (event.end) fields.end = { ...event.end };
  if (event.recurrence?.length) fields.recurrence = event.recurrence;
  if (event.extendedProperties?.private && Object.keys(event.extendedProperties.private).length) fields.extendedProperties = { private: { ...event.extendedProperties.private } };
  return fields;
}

/** The values `event` had for the fields in `fields`: what Undo writes back. */
function revertFields(event: GoogleEvent, fields: GoogleWrite): GoogleWrite {
  const back: GoogleWrite = {};
  if (fields.summary !== undefined) back.summary = event.summary ?? "";
  if (fields.description !== undefined) back.description = event.description ?? "";
  if (fields.location !== undefined) back.location = event.location ?? "";
  const time = (value: GoogleTime | undefined) => ({ date: value?.date ?? null, dateTime: value?.dateTime ?? null, timeZone: value?.timeZone ?? null });
  if (fields.start) back.start = time(event.start);
  if (fields.end) back.end = time(event.end);
  if (fields.recurrence !== undefined) back.recurrence = event.recurrence ?? [];
  if (fields.status) back.status = event.status;
  if (fields.extendedProperties) {
    back.extendedProperties = { private: Object.fromEntries(Object.keys(fields.extendedProperties.private).map((key) => [key, event.extendedProperties?.private?.[key] ?? ""])) };
  }
  return back;
}

const pendingId = () => crypto.randomUUID();
const titleOf = (event: GoogleEvent | null | undefined) => event?.summary?.trim() || "Untitled event";

function patchChange(calendarId: string, event: GoogleEvent, fields: GoogleWrite): PendingChange {
  return { id: pendingId(), change: { type: "patch", calendarId, eventId: event.id, fields }, local: withFields(event, fields), before: event, label: titleOf(withFields(event, fields)) };
}

function deleteChange(calendarId: string, event: GoogleEvent, local: GoogleEvent | null = null): PendingChange {
  return { id: pendingId(), change: { type: "delete", calendarId, eventId: event.id }, local, before: event, label: titleOf(event) };
}

/** The fields a patch writes, apart from times and the repeat rule. */
function plainFields(patch: GooglePatch): GoogleWrite {
  const fields: GoogleWrite = {};
  if (patch.title !== undefined) fields.summary = patch.title;
  if (patch.notes !== undefined) fields.description = patch.notes;
  if (patch.location !== undefined) fields.location = patch.location;
  if (patch.projectId !== undefined || patch.taskId !== undefined) {
    const props: Record<string, string> = {};
    if (patch.projectId !== undefined) props[GOOGLE_PROJECT_KEY] = patch.projectId ?? "";
    if (patch.projectId !== undefined || patch.taskId !== undefined) props[GOOGLE_TASK_KEY] = patch.taskId ?? "";
    fields.extendedProperties = { private: props };
  }
  return fields;
}

const changesTime = (patch: GooglePatch) => patch.start !== undefined || patch.end !== undefined || patch.allDay !== undefined;

/** An occurrence's span after `patch`. */
function nextSpan(occurrence: GoogleOccurrence, patch: GooglePatch): { start: number; end: number; allDay: boolean } {
  const allDay = patch.allDay ?? occurrence.allDay;
  const start = patch.start ?? occurrence.start;
  let end = patch.end ?? (start + (occurrence.end - occurrence.start));
  if (allDay) end = Math.max(end, shiftDays(dayStartOf(dayKey(start)), 1));
  return { start: allDay ? dayStartOf(dayKey(start)) : start, end, allDay };
}

function timeFields(span: { start: number; end: number; allDay: boolean }, zone: string, clear: boolean): GoogleWrite {
  return { start: writeTime(span.start, span.allDay, zone, clear), end: writeTime(span.end, span.allDay, zone, clear) };
}

/** The RRULE line a series has now, when it has one. */
const ruleLineOf = (event: GoogleEvent) => event.recurrence?.find((line) => /^RRULE[:;]/i.test(line)) ?? null;

/** The occurrence as an exception of its own, before any change: how "this event" edits start. */
function asException(occurrence: GoogleOccurrence): GoogleEvent {
  if (occurrence.event.recurringEventId || !occurrence.master || occurrence.originalStart === null) return occurrence.event;
  const { recurrence: _recurrence, ...master } = occurrence.master;
  const zone = master.start?.timeZone;
  const time = (at: number): GoogleTime => occurrence.allDay ? { date: dayKey(at) } : { dateTime: new Date(at).toISOString(), ...(zone ? { timeZone: zone } : {}) };
  return { ...master, id: occurrence.instanceId, recurringEventId: occurrence.master.id, originalStartTime: time(occurrence.originalStart), start: time(occurrence.start), end: time(occurrence.end) };
}

/** Exceptions of a series from `from` on, which a "this and following" change drops, as Google does. */
function exceptionsFrom(events: Readonly<Record<string, GoogleEvent>>, seriesId: string, from: number): GoogleEvent[] {
  // Removed occurrences stay removed: deleting them again would make Undo bring them back.
  return Object.values(events).filter((event) => event.recurringEventId === seriesId && event.status !== "cancelled" && (timeOf(event.originalStartTime)?.at ?? 0) >= from);
}

export function googleCreate(calendarId: string, fields: GoogleNewEvent, zone = deviceZone()): PendingChange {
  const span = { start: fields.start, end: fields.end, allDay: fields.allDay };
  const write: GoogleWrite = { ...plainFields({ title: fields.title, notes: fields.notes, location: fields.location }), ...timeFields(span, zone, false) };
  if (fields.projectId) write.extendedProperties = { private: { [GOOGLE_PROJECT_KEY]: fields.projectId, ...(fields.taskId ? { [GOOGLE_TASK_KEY]: fields.taskId } : {}) } };
  if (!write.location) delete write.location;
  if (!write.description) delete write.description;
  if (fields.repeat) write.recurrence = [rruleLine(fields.repeat, fields.start, fields.allDay)];
  const id = newGoogleId();
  const local = withFields({ id, status: "confirmed" }, write);
  return { id: pendingId(), change: { type: "insert", calendarId, eventId: id, fields: write }, local, before: null, label: titleOf(local) };
}

/**
 * Changes one occurrence, it and the ones after it, or the whole series, as `editOccurrence`
 * does for Events. Times in `patch` are the occurrence's new times; for "all" the series moves by
 * the same amount. "This and following" ends the series before the occurrence and starts a new
 * one from it, the way Google Calendar splits a series.
 */
export function googleEdit(events: Readonly<Record<string, GoogleEvent>>, occurrence: GoogleOccurrence, patch: GooglePatch, scope: EditScope, zone = deviceZone()): PendingChange[] {
  const { calendarId, master } = occurrence;
  const plain = plainFields(patch);
  const span = nextSpan(occurrence, patch);

  if (!occurrence.recurring || !master || scope === "this") {
    if (!occurrence.recurring && patch.repeat !== undefined) {
      // A single event that starts repeating becomes the first of its series.
      const recurrence = patch.repeat ? [rruleLine(patch.repeat, span.start, span.allDay)] : [];
      return [patchChange(calendarId, occurrence.event, { ...plain, ...timeFields(span, occurrence.event.start?.timeZone ?? zone, true), recurrence })];
    }
    const base = asException(occurrence);
    const fields = changesTime(patch) ? { ...plain, ...timeFields(span, base.start?.timeZone ?? zone, true) } : plain;
    return [patchChange(calendarId, base, fields)];
  }

  const seriesSpan = spanOf(master);
  const seriesZone = master.start?.timeZone ?? zone;
  if (!seriesSpan || occurrence.originalStart === null) return [];
  const rule = ruleLineOf(master);

  if (scope === "following" && occurrence.originalStart > seriesSpan.start && rule) {
    const changes: PendingChange[] = [patchChange(calendarId, master, { recurrence: withRule(master.recurrence, endRuleBefore(rule, seriesSpan.start, occurrence.originalStart, seriesSpan.allDay)) })];
    for (const exception of exceptionsFrom(events, master.id, occurrence.originalStart)) changes.push(deleteChange(calendarId, exception));
    const { recurrence: _recurrence, ...content } = contentFields(master);
    const recurrence = patch.repeat !== undefined
      ? (patch.repeat ? [rruleLine(patch.repeat, span.start, span.allDay)] : [])
      : [span.allDay === seriesSpan.allDay ? ruleFrom(rule, seriesSpan.start, occurrence.originalStart) : rruleLine(recurrenceOf(master).repeat ?? { freq: "weekly", interval: 1, weekdays: [], until: null, count: null }, span.start, span.allDay)];
    // Occurrences removed from the old part stay removed in the new one, moved along with it.
    if (recurrence.length && patch.repeat === undefined) {
      const shift = span.start - occurrence.originalStart;
      for (const removed of Object.values(events)) {
        const original = removed.recurringEventId === master.id && removed.status === "cancelled" ? timeOf(removed.originalStartTime)?.at : undefined;
        if (original === undefined || original < occurrence.originalStart) continue;
        recurrence.push(span.allDay ? `EXDATE;VALUE=DATE:${dateBasic(shiftDays(original, daysBetween(occurrence.originalStart, span.start)))}` : `EXDATE:${utcBasic(original + shift)}`);
      }
    }
    const fields: GoogleWrite = { ...content, ...plain, ...timeFields(span, seriesZone, false), ...(recurrence.length ? { recurrence } : {}) };
    if (fields.extendedProperties) fields.extendedProperties = { private: { ...content.extendedProperties?.private, ...plain.extendedProperties?.private } };
    const id = newGoogleId();
    const local = withFields({ id, status: "confirmed" }, fields);
    changes.push({ id: pendingId(), change: { type: "insert", calendarId, eventId: id, fields }, local, before: null, label: titleOf(local) });
    return changes;
  }

  // The whole series: fields change as they are; a time change moves every occurrence alike.
  const fields: GoogleWrite = { ...plain };
  let seriesStart = seriesSpan.start;
  let allDay = seriesSpan.allDay;
  if (changesTime(patch)) {
    allDay = span.allDay;
    let start: number, end: number;
    if (allDay) {
      start = shiftDays(dayStartOf(dayKey(seriesSpan.start)), daysBetween(occurrence.start, span.start));
      end = shiftDays(start, Math.max(1, daysBetween(span.start, span.end)));
    } else {
      // The first day stays; the time of day (and length) follow the occurrence.
      start = occurrence.allDay ? dayStartOf(dayKey(seriesSpan.start)) + (span.start - dayStartOf(dayKey(span.start))) : seriesSpan.start + (span.start - occurrence.start);
      end = start + (span.end - span.start);
    }
    seriesStart = start;
    Object.assign(fields, timeFields({ start, end, allDay }, seriesZone, true));
  }
  if (patch.repeat !== undefined) fields.recurrence = withRule(master.recurrence, patch.repeat ? rruleLine(patch.repeat, seriesStart, allDay) : null);
  else if (allDay !== seriesSpan.allDay && rule) {
    // UNTIL is written as a date for all-day series and as an instant for timed ones.
    const repeat = recurrenceOf(master).repeat;
    if (repeat) fields.recurrence = withRule(master.recurrence, rruleLine(repeat, seriesStart, allDay));
  }
  return [patchChange(calendarId, master, fields)];
}

/** Removes one occurrence, it and the ones after it, or the whole series. */
export function googleDelete(events: Readonly<Record<string, GoogleEvent>>, occurrence: GoogleOccurrence, scope: EditScope): PendingChange[] {
  const { calendarId, master } = occurrence;
  if (!occurrence.recurring) return [deleteChange(calendarId, occurrence.event)];
  if (scope === "this" || !master) {
    const exception = asException(occurrence);
    return [deleteChange(calendarId, exception, { id: exception.id, status: "cancelled", ...(exception.recurringEventId ? { recurringEventId: exception.recurringEventId } : {}), ...(exception.originalStartTime ? { originalStartTime: exception.originalStartTime } : {}) })];
  }
  const seriesSpan = spanOf(master);
  const rule = ruleLineOf(master);
  if (scope === "following" && seriesSpan && rule && occurrence.originalStart !== null && occurrence.originalStart > seriesSpan.start) {
    return [
      patchChange(calendarId, master, { recurrence: withRule(master.recurrence, endRuleBefore(rule, seriesSpan.start, occurrence.originalStart, seriesSpan.allDay)) }),
      ...exceptionsFrom(events, master.id, occurrence.originalStart).map((exception) => deleteChange(calendarId, exception))
    ];
  }
  return [deleteChange(calendarId, master)];
}

/** Moves a single event, or a whole series, to another calendar. */
export function googleMove(occurrence: GoogleOccurrence, destination: string): PendingChange[] {
  const event = occurrence.master ?? occurrence.event;
  if (destination === occurrence.calendarId || event.recurringEventId) return [];
  return [{ id: pendingId(), change: { type: "move", calendarId: occurrence.calendarId, eventId: event.id, destination }, local: event, before: event, label: titleOf(event) }];
}

/** The changes that undo `changes`, in the order to send them. */
export function invertChanges(changes: readonly PendingChange[]): PendingChange[] {
  return [...changes].reverse().map((item): PendingChange => {
    const { change } = item;
    switch (change.type) {
      case "insert":
        return { id: pendingId(), change: { type: "delete", calendarId: change.calendarId, eventId: change.eventId }, local: null, before: item.local, label: item.label };
      case "move":
        return { id: pendingId(), change: { type: "move", calendarId: change.destination, eventId: change.eventId, destination: change.calendarId }, local: item.before, before: item.local, label: item.label };
      case "delete": {
        const before = item.before!;
        return { id: pendingId(), change: { type: "patch", calendarId: change.calendarId, eventId: change.eventId, fields: contentFields(before), restore: true }, local: before, before: item.local, label: item.label };
      }
      case "patch": {
        const before = item.before!;
        return { id: pendingId(), change: { type: "patch", calendarId: change.calendarId, eventId: change.eventId, fields: revertFields(before, change.fields) }, local: before, before: item.local, label: item.label };
      }
    }
  });
}

/** Key for remembering a Project for a series ontrack can't write to. */
export function googleSeriesKey(calendarId: string, seriesId: string): string {
  return `${calendarId}|${seriesId}`;
}

function hash(text: string): string {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) value = Math.imul(value ^ text.charCodeAt(index), 16777619);
  return (value >>> 0).toString(36);
}

/** The `eventId` Time Entries logged from a Google event carry: its calendar and series. */
export function googleLinkId(calendarId: string, seriesId: string): string {
  return `google:${hash(calendarId)}:${seriesId.slice(0, 380)}`;
}
